import { useState, useEffect } from 'react';
import { ArrowRight, Play, ChevronLeft, ChevronRight, Pause, PlayCircle } from 'lucide-react';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Featured content carousel items
  const featuredItems = [
    {
      id: 1,
      title: "Breaking the Glass Ceiling: Women Leading Tech Innovation",
      description: "Discover how our community is reshaping the technology landscape through mentorship, education, and unwavering support for women in every stage of their tech careers.",
      image: "/cybelle/php.png",
      tag: "FEATURED",
      type: "article",
      readTime: "5 min read"
    },
    {
      id: 2,
      title: "AI Revolution: How Women Are Shaping the Future",
      description: "Explore the groundbreaking contributions women are making in artificial intelligence and machine learning, from research to real-world applications.",
      image: "/cybelle/ana.png",
      tag: "TRENDING",
      type: "article",
      readTime: "4 min read"
    },
    {
      id: 3,
      title: "From Code to CEO: Success Stories That Inspire",
      description: "Inspiring journeys of women who started as developers and grew into leadership roles, transforming tech companies along the way.",
        image: "/cybelle/code.png",
      tag: "INSPIRATION",
      type: "article",
      readTime: "6 min read"
    },
    {
      id: 4,
      title: "Cybersecurity: Women at the Frontlines",
      description: "Meet the women protecting our digital world and learn about career opportunities in this rapidly growing field.",
     image: "/cybelle/cyber.png",
      tag: "HOT TOPIC",
      type: "article",
      readTime: "3 min read"
    }
  ];

  // Side content items
  const sideItems = [
    {
      id: 1,
      title: "Connect with Industry Leaders",
      description: "One-on-one guidance from tech executives",
      image: "/cybelle/cci.png",
      tag: "MENTORSHIP",
      type: "article"
    },
    {
      id: 2,
      title: "Watch: A Day in Tech",
      description: "Inside look at careers in technology",
      image: "https://download.resumeble.com/images/blogs/guest/249bf85728-ai-specialist.jpg",
      tag: "VIDEO",
      type: "video"
    },
    {
      id: 3,
      title: "Upcoming Tech Events",
      description: "Conferences, workshops, and networking opportunities",
      image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&h=400&fit=crop",
      tag: "EVENTS",
      type: "article"
    },
    {
      id: 4,
      title: "Career Growth Resources",
      description: "Tools and guides for professional development",
     image: "/cybelle/gc.png",
      tag: "RESOURCES",
      type: "article"
    }
  ];

  const stats = [
    { value: "10K+", label: "Community Members" },
    { value: "500+", label: "Industry Mentors" },
    { value: "200+", label: "Companies Partnered" },
    { value: "95%", label: "Success Rate" }
  ];

  // Auto-play carousel
  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % featuredItems.length);
      }, 5000); // Change slide every 5 seconds
    }
    return () => clearInterval(interval);
  }, [isPlaying, featuredItems.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredItems.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + featuredItems.length) % featuredItems.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    }
    
    if (isRightSwipe) {
      prevSlide();
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };

  return (
    <>
      <section id="home" className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Main Featured Carousel */}
            <div className="lg:col-span-8">
              <div 
                className="relative h-[500px] rounded-2xl overflow-hidden group cursor-pointer"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {/* Featured Items */}
                <div className="relative h-full">
                  {featuredItems.map((item, index) => (
                    <div
                      key={item.id}
                      className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                        index === currentSlide
                          ? 'opacity-100 translate-x-0'
                          : 'opacity-0 translate-x-full'
                      }`}
                    >
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-8">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="inline-block px-3 py-1 text-xs font-semibold bg-white text-gray-900 rounded">
                            {item.tag}
                          </span>
                          <span className="text-gray-300 text-sm">{item.readTime}</span>
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 leading-tight">
                          {item.title}
                        </h2>
                        <p className="text-gray-200 text-sm md:text-base mb-4 leading-relaxed max-w-2xl">
                          {item.description}
                        </p>
                        <button className="inline-flex items-center gap-2 text-white font-semibold text-sm hover:gap-3 transition-all group/read">
                          Read Full Story
                          <ArrowRight className="w-4 h-4 group-hover/read:translate-x-1 transition-transform" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Navigation Arrows */}
                <button
                  onClick={prevSlide}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 backdrop-blur-sm text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 backdrop-blur-sm text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100"
                  aria-label="Next slide"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>

                {/* Slide Indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {featuredItems.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentSlide
                          ? 'w-8 bg-white'
                          : 'bg-white/50 hover:bg-white/80'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>

                {/* Play/Pause Button */}
                <button
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="absolute top-4 right-4 w-10 h-10 bg-black/50 backdrop-blur-sm text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-all"
                  aria-label={isPlaying ? "Pause carousel" : "Play carousel"}
                >
                  {isPlaying ? (
                    <Pause className="w-5 h-5" />
                  ) : (
                    <PlayCircle className="w-5 h-5" />
                  )}
                </button>
              </div>

              {/* Progress Bar */}
              <div className="mt-4 h-1 w-full bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-gray-900 transition-all duration-5000 ${
                    isPlaying ? 'animate-progress' : 'animation-paused'
                  }`}
                  style={{
                    animationPlayState: isPlaying ? 'running' : 'paused'
                  }}
                />
              </div>

              {/* Carousel Dots for Mobile */}
              <div className="flex justify-center gap-2 mt-4 lg:hidden">
                {featuredItems.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-8 h-2 rounded-full ${
                      index === currentSlide
                        ? 'bg-gray-900'
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Side Content Carousel */}
            <div className="lg:col-span-4">
              <div className="relative h-[500px] rounded-2xl overflow-hidden">
                {/* Side Items */}
                <div className="relative h-full">
                  {sideItems.map((item, index) => (
                    <div
                      key={item.id}
                      className={`absolute inset-0 transition-all duration-500 ease-in-out ${
                        index === currentSlide % sideItems.length
                          ? 'opacity-100 translate-y-0'
                          : 'opacity-0 -translate-y-full'
                      }`}
                    >
                      <div className="relative h-[240px] rounded-2xl overflow-hidden group cursor-pointer mb-6">
                        {item.type === 'video' ? (
                          <>
                            <div className="absolute inset-0 bg-gray-900 flex items-center justify-center z-10">
                              <Play className="w-12 h-12 text-white opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" />
                            </div>
                            <img
                              src={item.image}
                              alt={item.title}
                              className="w-full h-full object-cover opacity-60"
                            />
                          </>
                        ) : (
                          <img
                            src={item.image}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <span className="inline-block px-2 py-1 text-xs font-semibold bg-white/90 text-gray-900 rounded mb-2">
                            {item.tag}
                          </span>
                          <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                          <p className="text-gray-200 text-sm">{item.description}</p>
                        </div>
                      </div>

                      {/* Second side item for each main slide */}
                      {sideItems[(index + 1) % sideItems.length] && (
                        <div className="relative h-[240px] rounded-2xl overflow-hidden group cursor-pointer">
                          <img
                            src={sideItems[(index + 1) % sideItems.length].image}
                            alt={sideItems[(index + 1) % sideItems.length].title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                          <div className="absolute bottom-0 left-0 right-0 p-6">
                            <span className="inline-block px-2 py-1 text-xs font-semibold bg-white/90 text-gray-900 rounded mb-2">
                              {sideItems[(index + 1) % sideItems.length].tag}
                            </span>
                            <h3 className="text-xl font-bold text-white mb-2">
                              {sideItems[(index + 1) % sideItems.length].title}
                            </h3>
                            <p className="text-gray-200 text-sm">
                              {sideItems[(index + 1) % sideItems.length].description}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Side Carousel Navigation */}
                <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {sideItems.slice(0, 2).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentSlide % 2
                          ? 'w-6 bg-gray-900'
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                      aria-label={`Go to side content ${index + 1}`}
                    />
                  ))}
                </div>
              </div>

              {/* Manual Navigation for Side Content (Mobile) */}
              <div className="flex justify-between items-center mt-8 lg:hidden">
                <button
                  onClick={() => setCurrentSlide(prev => (prev - 1 + featuredItems.length) % featuredItems.length)}
                  className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span className="text-sm font-medium">Previous</span>
                </button>
                <div className="text-sm text-gray-600">
                  {currentSlide + 1} / {featuredItems.length}
                </div>
                <button
                  onClick={nextSlide}
                  className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
                >
                  <span className="text-sm font-medium">Next</span>
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-16 border-t border-gray-200">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center transform transition-all duration-500 hover:scale-105 group"
              >
                <div 
                  className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 group-hover:text-gray-700 transition-colors"
                  style={{
                    animationDelay: `${index * 100}ms`
                  }}
                >
                  {stat.value}
                </div>
                <div className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          {/* Carousel Info */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>Use arrow keys, swipe, or click dots to navigate • Auto-play: {isPlaying ? 'ON' : 'OFF'}</p>
          </div>
        </div>

        <style jsx>{`
          @keyframes progress {
            0% { width: 0%; }
            100% { width: 100%; }
          }
          
          .animate-progress {
            animation: progress 5s linear forwards;
          }
          
          .animation-paused {
            animation-play-state: paused;
          }
        `}</style>
      </section>
    </>
  );
};

export default HeroSection;